<template>
  <div>
    <v-navigation-drawer
      right
      clipped
      app
      stateless
      :value="drawer"
      :width="570"
    >
      <div
        v-if="loading || event.loading"
        class="pa-5"
      >
        <v-skeleton-loader
          type="text"
          max-height="45px"
          max-width="250px"
          class="mb-3"
        />
        <v-skeleton-loader
          type="text"
          max-height="45px"
        />
      </div>
      <div
        v-else-if="event"
        class="grey--text text--darken-2"
      >
        <div class="pt-6 px-6">
          <v-row
            no-gutters
            align="center"
          >
            <v-col>
              <h6 class="text-h6">
                {{ event.title }}
                <v-chip
                  label
                  :color="eventTypes[event.event_type].color"
                  dark
                  small
                >
                  {{ eventTypes[event.event_type].text }}
                </v-chip>
              </h6>
            </v-col>
            <v-col cols="auto">
              <v-btn
                v-if="$can('edit-event')"
                icon
                class="ma-0"
                :disabled="event.editing_rules && !event.editing_rules.can_edit_or_delete"
                @click="editEvent(event)"
              >
                <v-icon small>
                  fal fa-pen
                </v-icon>
              </v-btn>
              <v-menu
                v-if="!hasNoPlayers && (
                  (user.organisation_settings.has_regular_reports && event.can_have_pdf_reports && $can('view-performance')) ||
                  $can('view-event-notes') ||
                  event.event_type === eventTypeMatch
                )"
                offset-y
              >
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    class="ma-0"
                    v-on="on"
                  >
                    <v-icon small>
                      far fa-ellipsis-vertical
                    </v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item
                    v-if="user.organisation_settings.has_regular_reports && event.can_have_pdf_reports && $can('view-performance')"
                    :disabled="!event.pdf_report_download_url"
                    :href="event.pdf_report_download_url"
                    target="_blank"
                  >
                    <v-list-item-title>
                      {{ $t('event_details.download_pdf_report') }}
                    </v-list-item-title>
                    <v-list-item-icon>
                      <v-icon small>
                        fal fa-download
                      </v-icon>
                    </v-list-item-icon>
                  </v-list-item>
                  <v-tooltip
                    v-if="user.organisation_settings.has_regular_reports && event.can_have_pdf_reports && $can('view-performance')"
                    top
                  >
                    <template v-slot:activator="{ on }">
                      <v-list-item
                        @click="regeneratePdfReport()"
                        v-on="on"
                      >
                        <v-list-item-title v-if="event.pdf_report_download_url">
                          {{ $t('event_details.regenerate_pdf_report') }}
                        </v-list-item-title>
                        <v-list-item-title v-else>
                          {{ $t('event_details.generate_pdf_report') }}
                        </v-list-item-title>
                        <v-list-item-icon>
                          <v-icon small>
                            fal {{ !event.pdf_report_download_url ? 'fa-file-lines' : 'fa-refresh' }}
                          </v-icon>
                        </v-list-item-icon>
                      </v-list-item>
                    </template>
                    <span>
                      {{ $t('event_details.pdf_report.label') }}
                    </span>
                  </v-tooltip>
                  <v-list-item
                    v-if="$can('view-event-notes')"
                    :href="downloadEventPlanLink"
                    target="_blank"
                  >
                    <v-list-item-title>
                      {{
                        $t('event_details.download_event_plan', { event_type: $t('event_type.' + event.event_type) })
                      }}
                    </v-list-item-title>
                    <v-list-item-icon>
                      <v-icon small>
                        fal fa-download
                      </v-icon>
                    </v-list-item-icon>
                  </v-list-item>
                  <v-list-item
                    v-if="event.event_type === eventTypeMatch"
                    :href="downloadMatchPreparationLink"
                    target="_blank"
                  >
                    <v-list-item-title>
                      {{ $t('event_details.download_match_preparation_PDF') }}
                    </v-list-item-title>
                    <v-list-item-icon>
                      <v-icon small>
                        fal fa-download
                      </v-icon>
                    </v-list-item-icon>
                  </v-list-item>
                </v-list>
              </v-menu>
              <v-btn
                icon
                class="ma-0"
                @click="drawer = false"
              >
                <v-icon small>
                  fa fa-times
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <div class="grey--text mt-1 text--darken-1">
            {{ $dayjs(event.start_at).format(getDateTimeFormatForUser('dddd, MMMM D')) }} &middot;
            {{ $dayjs(event.start_at).format(getDateTimeFormatForUser('HH:mm')) }} -
            {{ $dayjs(event.end_at).format(getDateTimeFormatForUser('HH:mm')) }}
          </div>
          <div class="mt-2">
            <v-row no-gutters>
              <v-col>
                <v-chip
                  v-for="team in event.teams"
                  :key="'chip_team_'+team.id"
                  label
                  outlined
                >
                  {{ team.name }}
                </v-chip>
              </v-col>
              <v-col
                v-if="eventView && !hasNoPlayers && event.performs_performance_analysis && (!user.coach || event.coaches.some(coach => coach.id === user.coach.id) || event.teams.some(eventTeam => user.coach && user.coach.teams && user.coach.teams.some(userTeam => userTeam.id === eventTeam.id)))"
                cols="auto"
              >
                <v-btn
                  v-if="dayjs().isAfter(event.start_at) && $can('view-performance')"
                  text
                  color="primary"
                  class="text-none"
                  small
                  :to="{ name: $routeNames.PERFORMANCE_EVENT, query: { event_id: event.id } }"
                >
                  {{ $t('button.view_analysis') }}
                  <v-icon
                    right
                    small
                  >
                    fa fa-arrow-right
                  </v-icon>
                </v-btn>
                <v-btn
                  v-else-if="dayjs().isBefore(event.start_at) && event.event_type === 'match'"
                  text
                  color="primary"
                  class="text-none"
                  small
                  @click="matchPrepareModal = true"
                >
                  {{ $t('button.prepare_match') }}
                  <v-icon
                    right
                    small
                  >
                    fal fa-court-sport
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </div>
          <div v-if="event.notes && event.notes.filter(n => n.is_pinned && (!n.players || n.players.length === 0)).length > 0 && $can('view-event-notes')">
            <v-card
              v-for="note in event.notes.filter(n => n.is_pinned && (!n.players || n.players.length === 0))"
              :key="note.id"
              outlined
              class="pa-3 mt-3"
            >
              <v-row
                no-gutters
                align="start"
              >
                <v-col>
                  <div
                    class="rich-text"
                    v-html="note.text"
                  />
                </v-col>
              </v-row>

              <div
                v-if="note.is_player_visible"
                class="caption grey--text d-inline"
              >
                <v-icon
                  x-small
                  color="grey"
                >
                  fal fa-eye
                </v-icon>
                {{ $t('visible_by_player.info') }}
              </div>
              <div
                v-else-if="!hasNoPlayers"
                class="caption grey--text d-inline"
              >
                <v-icon
                  x-small
                  color="grey"
                >
                  fal fa-eye-slash
                </v-icon>
                {{ $t('card_title.not_visible_by_players') }}
              </div>
            </v-card>
          </div>

          <v-card
            v-if="event.training_program_instance"
            outlined
            class="py-2 px-3 mt-3"
          >
            <div class="d-inline-flex align-center text-body-1 grey--text">
              <v-icon
                small
                color="grey"
                class="mr-2"
              >
                fal fa-list
              </v-icon>
              <div>
                {{ $t('event_details.part_of') }} <a
                  href=""
                  @click.stop.prevent="launchTrainingProgramInstance(event.training_program_instance.id)"
                >{{ event.training_program_instance.training_program.name }}</a> {{
                  $t('event_details.training_program')
                }}
              </div>
            </div>
          </v-card>
          <v-row
            no-gutters
            align="center"
            class="mt-3"
          >
            <v-col cols="auto">
              <v-tabs v-model="tab">
                <v-tab
                  v-if="!hasNoPlayers"
                  class="text-none text-body-1"
                  href="#players"
                  @click="tab = 'players'"
                >
                  {{ $t('event_details.players') }}
                </v-tab>
                <v-tab
                  v-if="!hasNoPlayers"
                  class="text-none text-body-1"
                  href="#periods"
                  @click="tab = 'periods'"
                >
                  {{ $t('event_details.periods') }}
                </v-tab>
                <v-tab
                  v-if="$can('view-event-notes')"
                  class="text-none text-body-1"
                  href="#notes"
                  @click="tab = 'notes'"
                >
                  {{ $t('event_details.notes') }}
                </v-tab>
                <v-tab
                  v-if="event.training_protocol_id && !hasNoPlayers && $can('view-training-protocols')"
                  class="text-none text-body-1"
                  href="#training"
                  @click="tab = 'training'"
                >
                  {{ $t('performance.advanced.training') }}
                </v-tab>
                <v-tab
                  v-if="user.organisation_settings.has_drill_module"
                  class="text-none text-body-1"
                  href="#drills"
                  @click="tab = 'drills'"
                >
                  {{ $t('event_details.drills') }}
                </v-tab>
                <v-tab
                  v-if="$can('view-event-media')"
                  class="text-none text-body-1"
                  href="#media"
                  @click="tab = 'media'"
                >
                  {{ $t('event_details.media') }}
                </v-tab>
                <v-tab
                  v-if="fieldwizIntegration && event.editing_rules.integrations_enabled && $can('create-performance')"
                  class="text-none text-body-1"
                  href="#fieldwiz"
                  @click="tab = 'fieldwiz'"
                >
                  {{ $t('event_details.fieldwiz') }}
                </v-tab>
                <v-tab
                  v-if="statsportIntegration && event.editing_rules.integrations_enabled && $can('create-performance')"
                  class="text-none text-body-1"
                  href="#statsport"
                  @click="tab = 'statsport'"
                >
                  {{ $t('event_details.statsports') }}
                </v-tab>
                <v-tab
                  v-if="fitogetherIntegration && event.editing_rules.integrations_enabled && $can('create-performance')"
                  class="text-none text-body-1"
                  href="#fitogether"
                  @click="tab = 'fitogether'"
                >
                  {{ $t('event_details.fitogether') }}
                </v-tab>
                <v-tab
                  v-if="playertekIntegration && event.editing_rules.integrations_enabled && $can('create-performance')"
                  class="text-none text-body-1"
                  href="#playertek"
                  @click="tab = 'playertek'"
                >
                  {{ $t('event_details.playertek') }}
                </v-tab>
                <v-tab
                  v-if="catapultOneIntegration && event.editing_rules.integrations_enabled && $can('create-performance')"
                  class="text-none text-body-1"
                  href="#catapult_one"
                  @click="tab = 'catapult_one'"
                >
                  {{ $t('event_details.catapult_one') }}
                </v-tab>
                <v-tab
                  v-if="pitcherogpsIntegration && event.editing_rules.integrations_enabled && $can('create-performance')"
                  class="text-none text-body-1"
                  href="#pitcherogps"
                  @click="tab = 'pitcherogps'"
                >
                  {{ $t('event_details.pitcherogps') }}
                </v-tab>
              </v-tabs>
            </v-col>
          </v-row>
        </div>
        <v-divider />

        <div v-if="tab === 'players'">
          <div
            v-if="event.players.length === 0"
            class="pa-6"
          >
            <v-card
              class="grey--text text-center pa-4 text-body-1"
              outlined
            >
              {{ $t('event_details.no_players') }}
            </v-card>
          </div>
          <v-list
            v-else
            dense
          >
            <v-list-item
              v-for="player in event.players"
              :key="'event_player_'+player.id"
              class="v-list--bordered px-6"
              @click="eventPlayerDialog.playerId = player.id; eventPlayerDialog.visible = true"
            >
              <v-list-item-content>
                <v-list-item-title>
                  {{ player.last_name }}, {{ player.first_name }} {{ player.is_captain ? '(C)' : '' }}
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <div class="d-inline-flex align-center">
                  <v-tooltip
                    v-if="player.player_match_role"
                    top
                    class="mr-3"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <div
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon
                          class="mr-3 pb-1"
                          small
                          style="width: 24px; height: 24px"
                        >
                          fa-light {{ getIconForMatchRole(player.player_match_role.short_name) }}
                        </v-icon>
                      </div>
                    </template>
                    <span>
                      {{ $t(`player_match_roles.${player.player_match_role.short_name}`) }}
                    </span>
                  </v-tooltip>

                  <v-tooltip
                    v-if="hasItlModule && event.editing_rules.feedback_enabled && (!player.feedback_request || !player.feedback_request.submitted_at) && $dayjs(event.end_at).isAfter($dayjs().subtract(6, 'hour'))"
                    top
                    max-width="400px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <div
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-switch
                          v-model="player.is_feedback_notification_enabled"
                          class="d-inline-block"
                          hide-details
                          inset
                          dense
                          @change="togglePlayerFeedbackNotification(player, $event)"
                          @click.prevent.stop
                        />
                      </div>
                    </template>
                    <span>
                      {{ $t('switch.add_feedback.tooltip') }}
                    </span>
                  </v-tooltip>
                  <span v-if="hasItlModule && event.editing_rules.feedback_enabled">
                    <v-btn
                      v-if="user.organisation_settings.can_submit_feedback_by_coach && $can('submit-event-feedback') && (!player.feedback_request || !player.feedback_request.submitted_at) && $dayjs(event.start_at).isBefore($dayjs()) && $dayjs(event.end_at).isAfter($dayjs().subtract(3, 'day'))"
                      small
                      text
                      class="text-none mr-2"
                      color="primary"
                      @click.stop="addEventFeedback.player = player; addEventFeedback.dialog = true"
                    >
                      {{ $t('switch.add_feedback') }}
                    </v-btn>
                    <v-chip
                      v-if="player.has_attended === null || (player.has_attended && $dayjs(event.end_at).isBefore($dayjs().subtract(3, 'day')))"
                      label
                      color="grey lighten-3"
                      text-color="grey darken-1"
                      small
                    >
                      {{ $t('event_details.no_feedback') }}
                    </v-chip>
                    <v-chip
                      v-else-if="player.has_attended === false"
                      label
                      color="error"
                      dark
                      small
                    >
                      {{ $t('card_title.did_not_attend') }}:
                      <strong
                        class="ml-1"
                      >{{
                        player.event_missed_reason ? $t('not_attended_reason.' + toCamelCase(player.event_missed_reason.reason)) : 'Unknown reason'
                      }}</strong>
                    </v-chip>
                    <v-chip
                      v-else-if="player.feedback_request && player.feedback_request.submitted_at"
                      label
                      color="success"
                      dark
                      small
                    >
                      {{ $t('event_details.feedback_submitted') }}
                    </v-chip>
                    <v-chip
                      v-else
                      label
                      color="grey"
                      dark
                      small
                    >
                      {{ $t('card_title.waiting_for_feedback') }}
                    </v-chip>
                  </span>
                  <span v-else-if="!hasItlModule">
                    <v-chip
                      v-if="player.has_attended === false"
                      label
                      color="error"
                      dark
                      small
                    >
                      {{ $t('card_title.did_not_attend') }}:
                      <strong
                        class="ml-1"
                      >{{ player.event_missed_reason ? $t('not_attended_reason.' + toCamelCase(player.event_missed_reason.reason)) : 'Unknown reason' }}</strong>
                    </v-chip>
                    <v-chip
                      v-if="player.has_attended === true"
                      label
                      color="success"
                      dark
                      small
                    >
                      {{ $t('attended.label') }}
                    </v-chip>
                  </span>
                  <v-icon
                    v-if="$can('view-player-media')"
                    class="ml-2"
                    x-small
                    :color="player.media.length > 0 ? 'primary' : 'grey lighten-1'"
                  >
                    {{ player.media.length > 0 ? 'far' : 'fal' }} fa-paperclip
                  </v-icon>
                  <v-icon
                    v-if="player.has_training_exercises && $can('view-training-protocols')"
                    class="ml-2"
                    x-small
                    color="grey lighten-1"
                  >
                    fal fa-dumbbell
                  </v-icon>
                  <v-icon
                    v-if="$can('view-player-notes')"
                    class="ml-2"
                    x-small
                    :color="player.notes.length > 0 ? 'primary' : 'grey lighten-1'"
                  >
                    {{ player.notes.length > 0 ? 'far' : 'fal' }} fa-clipboard-list
                  </v-icon>
                </div>
              </v-list-item-action>
            </v-list-item>
          </v-list>

          <div v-if="hasOpenEventApplications && event.is_open_application">
            <div class="subtitle-1 grey--text text--darken-3 font-weight-medium px-6 mt-8 mb-4">
              {{ $t('event_details.applications.waiting_list.label') }}
            </div>

            <div
              v-if="event.waiting_list_players.length === 0"
              class="px-6"
            >
              <v-card
                class="grey--text text-center pa-4 text-body-1"
                outlined
              >
                {{ $t('event_details.applications.waiting_list.no_players') }}
              </v-card>
            </div>
            <v-list
              v-else
              dense
              class="pa-0"
            >
              <v-list-item
                v-for="(player, index) in event.waiting_list_players"
                :key="'wl_event_player_'+player.id"
                class="v-list--bordered px-6"
              >
                <v-list-item-icon>
                  <v-avatar
                    size="24px"
                    color="grey darken-2"
                    class="text-body-1 white--text font-weight-bold"
                  >
                    {{ index + 1 }}
                  </v-avatar>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ player.last_name }}, {{ player.first_name }}
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action class="text-body-1 grey--text">
                  {{ $dayjs(player.added_at).format(getDateTimeFormatForUser('ll, HH:mm:ss')) }}
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>
        </div>

        <div v-if="tab === 'fieldwiz'">
          <div class="px-6 py-2">
            <event-source-item
              :label="$t('event_details.source_item.DKG')"
              :item="event.fieldwiz_id"
              :file-upload="fileUpload"
              @replace="$refs.uploadFile.click()"
            />
            <v-row>
              <v-col class="text-body-1">
                {{ $t("card_dialog.manage_devices.title") }}
              </v-col>
              <v-col cols="auto">
                <v-btn
                  text
                  small
                  color="primary"
                  class="text-none"
                  @click="devicesDialog = true"
                >
                  {{ $t("button.manage") }}
                </v-btn>
              </v-col>
            </v-row>
            <input
              id="upload-file"
              ref="uploadFile"
              type="file"
              accept=".dkg,.asi"
              style="display: none"
              @change="handleFileChange"
            >
          </div>
        </div>
        <div v-if="tab === 'statsport'">
          <div class="px-6 py-2">
            <event-source-item
              :label="$t('event_details.source_item.CSV')"
              :item="event.statsport_csv_url"
              :file-upload="statsportFileUpload"
              @replace="$refs.statsportUploadFile.click()"
            />
            <input
              id="statsport-upload-file"
              ref="statsportUploadFile"
              type="file"
              style="display: none"
              @input="handleStatsportFileChange"
            >
          </div>
        </div>
        <div v-if="tab === 'fitogether'">
          <div class="px-6 py-2">
            <event-source-item
              :label="$t('event_details.source_item.CSV')"
              :item="event.fitogether_csv_url"
              :file-upload="fitogetherFileUpload"
              @replace="$refs.fitogetherUploadFile.click()"
            />
            <input
              id="fitogether-upload-file"
              ref="fitogetherUploadFile"
              type="file"
              style="display: none"
              @input="handleFitogetherFileChange"
            >
          </div>
        </div>
        <div v-if="tab === 'playertek'">
          <div class="px-6 py-2">
            <event-source-item
              :label="$t('event_details.source_item.CSV')"
              :item="event.playertek_csv_url"
              :file-upload="playertekFileUpload"
              @replace="$refs.playertekUploadFile.click()"
            />
            <input
              id="playertek-upload-file"
              ref="playertekUploadFile"
              type="file"
              style="display: none"
              @input="handlePlayertekFileChange"
            >
          </div>
        </div>
        <div v-if="tab === 'catapult_one'">
          <div class="px-6 py-2">
            <event-source-item
              :label="$t('event_details.source_item.CSV')"
              :item="event.catapult_one_csv_url"
              :file-upload="catapultOneFileUpload"
              @replace="$refs.catapultOneUploadFile.click()"
            />
            <input
              id="catapult-one-upload-file"
              ref="catapultOneUploadFile"
              type="file"
              style="display: none"
              @input="handleCatapultOneFileChange"
            >
          </div>
        </div>
        <div v-if="tab === 'pitcherogps'">
          <div class="px-6 py-2">
            <event-source-item
              :label="$t('event_details.source_item.XLSX')"
              :item="event.pitcherogps_xlsx_url"
              :file-upload="pitcherogpsFileUpload"
              @replace="$refs.pitcherogpsUploadFile.click()"
            />
            <input
              id="pitcherogps-upload-file"
              ref="pitcherogpsUploadFile"
              type="file"
              style="display: none"
              @input="handlePitcherogpsFileChange"
            >
          </div>
        </div>
        <div v-if="tab === 'periods'">
          <div
            v-if="event.periods.length > 0"
            class="px-6 pt-6"
          >
            <v-row
              v-for="period in event.periods"
              :key="'show_period_' + period.id"
              class="v-list--bordered grey--text text--darken-1 text-body-2"
            >
              <v-col>
                {{ period.title }}
              </v-col>
              <v-col
                cols="auto"
                class="text-no-wrap"
              >
                {{ $dayjs(period.start_at).format(getDateTimeFormatForUser('HH:mm:ss')) }}
                -
                {{ $dayjs(period.end_at).format(getDateTimeFormatForUser('HH:mm:ss')) }}
              </v-col>
            </v-row>
          </div>

          <div
            v-if="$can('edit-event')"
            class="pa-6"
          >
            <v-btn
              outlined
              large
              block
              color="primary"
              @click="openPeriodEditor()"
            >
              {{ $t('event_details.set_event_periods') }}
            </v-btn>
          </div>
        </div>
        <div v-if="tab === 'notes'">
          <event-notes
            v-if="event"
            :event="event"
            @update="$emit('update')"
          />
        </div>
        <div v-if="tab === 'media'">
          <event-media
            v-if="event"
            :event="event"
            @update="$emit('update')"
            @update:media="event.media = $event"
          />
        </div>
        <div v-if="tab === 'training'">
          <event-training-protocol
            v-if="event"
            :event="event"
            @update="$emit('update')"
          />
        </div>
        <div v-if="tab === 'drills'">
          <event-drill :event="event" />
        </div>
      </div>
    </v-navigation-drawer>

    <add-event-feedback-dialog
      v-if="event && addEventFeedback.player"
      :visible.sync="addEventFeedback.dialog"
      :player="addEventFeedback.player"
      :event="event"
      @success="$emit('update')"
    />

    <event-player-dialog
      v-if="event && eventPlayerDialog.playerId"
      :visible.sync="eventPlayerDialog.visible"
      :player-id="eventPlayerDialog.playerId"
      :event="event"
      @success="$emit('update')"
    />

    <match-prepare-dialog
      v-if="event"
      :visible.sync="matchPrepareModal"
      :event-details="event"
    />

    <card-dialog
      v-model="periodEditor.visible"
      max-width="1000px"
      :title="$t('event_details.set_periods_event.dialog.header')"
    >
      <v-card-text v-if="event">
        <v-row
          no-gutters
          align="center"
          class="mt-4 mb-2"
        >
          <v-col>
            <subtitle>
              {{ event.title }}
              <v-icon
                small
                class="ml-2"
                @click="editEvent(event)"
              >
                fa fa-pen
              </v-icon>
            </subtitle>
            <subtitle class="mt-1">
              {{ $dayjs(event.start_at).format(getDateTimeFormatForUser('dddd, MMMM D')) }}
              &middot;
              <strong>{{ $dayjs(event.start_at).format(getDateTimeFormatForUser('HH:mm')) }} -
                {{ $dayjs(event.end_at).format(getDateTimeFormatForUser('HH:mm')) }}</strong>
            </subtitle>
          </v-col>
          <v-col cols="auto">
            <v-btn
              color="primary"
              small
              class="text-none"
              @click="addPeriod(); updatePlayerPeriodItems()"
            >
              <v-icon
                x-small
                left
              >
                fal fa-plus
              </v-icon>
              {{ $t('button.add_period') }}
            </v-btn>
          </v-col>
        </v-row>
        <v-card outlined>
          <v-card-text v-if="periodEditor.periods.length > 0">
            <v-row
              v-for="(period, index) in periodEditor.periods"
              :key="'main_period_editor_' + index"
              no-gutters
              align="center"
              class="mb-2"
            >
              <v-col
                cols="auto"
                class="pr-4"
              >
                <v-icon
                  small
                  color="red"
                  @click="deletePeriod(index)"
                >
                  fal fa-trash
                </v-icon>
              </v-col>
              <v-col cols="auto">
                <v-text-field
                  v-model="period.title"
                  style="width: 130px"
                  outlined
                  hide-details
                  dense
                />
              </v-col>
              <v-col class="px-4">
                <v-range-slider
                  v-model="period.range"
                  :min="periodEditor.min"
                  :max="periodEditor.max"
                  hide-details
                  :step="10"
                  track-color="grey lighten-3"
                />
              </v-col>
              <v-col
                cols="auto"
                class="text-no-wrap"
              >
                <v-text-field
                  style="width: 20px"
                  class="d-inline-block"
                  dense
                  hide-details
                  :value="$dayjs(event.start_at).add(period.range[0], 'seconds').format('HH')"
                  @change="inputStart(index, $event, 'hour')"
                />
                :
                <v-text-field
                  style="width: 20px"
                  class="d-inline-block"
                  dense
                  hide-details
                  :value="$dayjs(event.start_at).add(period.range[0], 'seconds').format('mm')"
                  @change="inputStart(index, $event, 'minute')"
                />
                :
                <v-text-field
                  style="width: 20px"
                  class="d-inline-block"
                  dense
                  hide-details
                  :value="$dayjs(event.start_at).add(period.range[0], 'seconds').format('ss')"
                  @change="inputStart(index, $event, 'second')"
                />
                <span class="px-1">
                  -
                </span>
                <v-text-field
                  style="width: 20px"
                  class="d-inline-block"
                  dense
                  hide-details
                  :value="$dayjs(event.start_at).add(period.range[1], 'seconds').format('HH')"
                  @change="inputEnd(index, $event, 'hour')"
                />
                :
                <v-text-field
                  style="width: 20px"
                  class="d-inline-block"
                  dense
                  hide-details
                  :value="$dayjs(event.start_at).add(period.range[1], 'seconds').format('mm')"
                  @change="inputEnd(index, $event, 'minute')"
                />
                :
                <v-text-field
                  style="width: 20px"
                  class="d-inline-block"
                  dense
                  hide-details
                  :value="$dayjs(event.start_at).add(period.range[1], 'seconds').format('ss')"
                  @change="inputEnd(index, $event, 'second')"
                />
              </v-col>
              <v-col
                v-if="event.event_type === eventTypeMatch || event.event_type === eventTypeTraining"
                cols="auto"
                class="pl-4"
              >
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <span v-on="on">
                      <v-switch
                        v-model="period.is_play_time"
                        dense
                      >
                        <template v-slot:label>
                          <span
                            class="text-body-1 grey--text text--darken-2"
                          >{{ $t('event_details.set_periods_event.dialog.is_play_time') }}</span>
                        </template>
                      </v-switch>
                    </span>
                  </template>
                  <span>
                    {{ $t('event_details.set_periods_event.dialog.play_time_tooltip') }}
                  </span>
                </v-tooltip>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-text
            v-else
            class="text-center py-6 grey--text text-body-1"
          >
            {{ $t('event_details.no_periods_defined.info') }}
          </v-card-text>
        </v-card>

        <v-row class="mt-3 mb-1">
          <v-col>
            <subtitle>
              {{ $t('event_details.set_periods_event.player_settings') }}
            </subtitle>
          </v-col>
        </v-row>

        <v-card
          v-for="player in periodEditor.players"
          :key="'player_' + player.id"
          outlined
          class="mb-1"
        >
          <v-card-text class="py-4 px-6">
            <v-row
              no-gutters
              align="center"
            >
              <v-col class="text-body-1">
                {{ player.first_name }} {{ player.last_name }}
                <v-btn
                  v-if="player.periods.length > 0 && (player.fieldwiz_id || player.catapult_id || player.gpexe_id || player.polar_team_id || player.garmin_id || player.polar_id || event.garmin_id || event.polar_id)"
                  small
                  color="primary"
                  class="text-none ml-2"
                  text
                  :loading="player.loading"
                  @click="fetchDownsampledData(player); player.showDownsampledData = true"
                >
                  {{ $t("button.show_activity_graph") }}
                </v-btn>
              </v-col>
              <v-col
                v-if="player.periods.length === 0 && periodEditor.periods.length > 0"
                cols="auto"
                class="caption grey--text pr-4"
              >
                {{ $t('label.periods_same_as_main') }} <a
                  href="#"
                  @click.stop="editPlayerPeriods(player)"
                >{{ $t('button.edit_player_periods') }}</a>
              </v-col>
              <v-col
                v-else-if="player.edit_periods"
                cols="auto"
              >
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <span v-on="player.periods.length >= periodEditor.periods.length ? on : undefined">
                      <v-btn
                        text
                        small
                        :disabled="player.periods.length >= periodEditor.periods.length"
                        @click="addPlayerPeriod(player)"
                      >
                        <v-icon
                          left
                          small
                        >
                          far fa-plus
                        </v-icon>
                        {{ $t('button.add_period') }}
                      </v-btn>
                    </span>
                  </template>
                  <span>
                    {{ $t('player_periods_main_event_periods.title') }}
                  </span>
                </v-tooltip>
              </v-col>
            </v-row>
            <div v-if="player.periods.length > 0 || player.edit_periods">
              <v-row
                v-for="(period, index) in player.periods"
                :key="'player_period_editor_' + player.id + index"
                no-gutters
                align="center"
                class="mb-2"
              >
                <v-col
                  cols="auto"
                  class="pr-4"
                >
                  <v-icon
                    small
                    color="red"
                    @click="deletePlayerPeriod(player, index)"
                  >
                    fal fa-trash
                  </v-icon>
                </v-col>
                <v-col style="z-index: 1;">
                  <v-select
                    v-model="period.parent_period_index"
                    style="width: 160px"
                    :items="period.availablePeriods"
                    item-value="index"
                    item-text="title"
                    :label="$t('dropdown.connected_to.title')"
                    outlined
                    hide-details
                    dense
                    @input="updatePlayerPeriodItems"
                  />
                </v-col>
                <v-col
                  cols="auto"
                  class="text-no-wrap"
                  style="z-index: 1;"
                >
                  <v-text-field
                    style="width: 20px"
                    class="d-inline-block"
                    dense
                    hide-details
                    :value="$dayjs(event.start_at).add(period.range[0], 'seconds').format('HH')"
                    @change="inputPlayerStart(player, index, $event, 'hour')"
                  />
                  :
                  <v-text-field
                    style="width: 20px"
                    class="d-inline-block"
                    dense
                    hide-details
                    :value="$dayjs(event.start_at).add(period.range[0], 'seconds').format('mm')"
                    @change="inputPlayerStart(player, index, $event, 'minute')"
                  />
                  :
                  <v-text-field
                    style="width: 20px"
                    class="d-inline-block"
                    dense
                    hide-details
                    :value="$dayjs(event.start_at).add(period.range[0], 'seconds').format('ss')"
                    @change="inputPlayerStart(player, index, $event, 'second')"
                  />
                  <span class="px-1">
                    -
                  </span>
                  <v-text-field
                    style="width: 20px"
                    class="d-inline-block"
                    dense
                    hide-details
                    :value="$dayjs(event.start_at).add(period.range[1], 'seconds').format('HH')"
                    @change="inputPlayerEnd(player, index, $event, 'hour')"
                  />
                  :
                  <v-text-field
                    style="width: 20px"
                    class="d-inline-block"
                    dense
                    hide-details
                    :value="$dayjs(event.start_at).add(period.range[1], 'seconds').format('mm')"
                    @change="inputPlayerEnd(player, index, $event, 'minute')"
                  />
                  :
                  <v-text-field
                    style="width: 20px"
                    class="d-inline-block"
                    dense
                    hide-details
                    :value="$dayjs(event.start_at).add(period.range[1], 'seconds').format('ss')"
                    @change="inputPlayerEnd(player, index, $event, 'second')"
                  />
                </v-col>
                <v-col
                  v-if="event.event_type === eventTypeMatch || event.event_type === eventTypeTraining"
                  cols="auto"
                  class="pl-4"
                  style="z-index: 1;"
                >
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <span v-on="on">
                        <v-switch
                          v-model="period.is_play_time"
                          dense
                        >
                          <template v-slot:label>
                            <span
                              class="text-body-1 grey--text text--darken-2"
                            >{{ $t('event_details.set_periods_event.dialog.is_play_time') }}</span>
                          </template>
                        </v-switch>
                      </span>
                    </template>
                    <span>
                      {{ $t('event_details.set_periods_event.dialog.play_time_tooltip') }}
                    </span>
                  </v-tooltip>
                </v-col>
                <v-col
                  cols="12"
                  class="px-4 pt-7"
                  style="position: relative;"
                >
                  <div
                    v-if="player.showDownsampledData"
                    style="position: absolute; top: 5px; left: 0; width: 100%; transform: translateY(-50%); z-index: 0; opacity: 0.25"
                  >
                    <line-chart
                      v-if="player.downsampledData.speed && player.downsampledData.speed.length > 0"
                      class="ma-0 pa-0 elevation-0"
                      :series-override="[{name: 'Speed', data: getSpeedSeries(player.downsampledData)}]"
                      :xaxis="{ type: 'datetime', labels: { show: false } }"
                      :yaxis="[{show: false}]"
                      :height="120"
                      :colors="['#BC291C']"
                      :tooltip="{enabled: false}"
                      :animations="{enabled: false}"
                      :grid="{show: false}"
                    />
                  </div>
                  <v-range-slider
                    v-model="period.range"
                    :min="periodEditor.min"
                    :max="periodEditor.max"
                    hide-details
                    :step="10"
                    track-color="grey lighten-3"
                    style="position: relative; z-index: 1;"
                  />
                </v-col>
              </v-row>
            </div>
          </v-card-text>
        </v-card>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="grey"
          text
          @click="periodEditor.visible = false"
        >
          {{ $t('button.cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          :loading="submitting"
          @click="storePeriods()"
        >
          <v-icon
            left
            small
          >
            fa fa-check
          </v-icon>
          {{ $t('button.submit') }}
        </v-btn>
      </v-card-actions>
    </card-dialog>

    <card-dialog
      v-model="devicesDialog"
      max-width="800px"
      :title="$t('card_dialog.manage_devices.title')"
    >
      <v-card-text class="pa-4">
        <v-list-item
          v-for="player in event.players"
          :key="'player_' + player.id"
          class="my-3"
        >
          <v-row
            no-gutters
            align="center"
          >
            <v-col class="text-body-1">
              {{ player.first_name }} {{ player.last_name }}
            </v-col>
            <v-col
              cols="auto"
            >
              <v-autocomplete
                v-model="player.device_id"
                :items="devices.data"
                item-text="serial_number"
                item-value="id"
                :label="$t('dropdown.serial_number.title')"
                outlined
                style="width: 160px"
                :placeholder="$t('dropdown.device_id.placeholder')"
                dense
                hide-details
                clearable
              >
                <template v-slot:item="{ item: device }">
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ device.serial_number }}
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ device.device_name }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>
        </v-list-item>
      </v-card-text>
      <template v-slot:actions>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn
            color="primary"
            class="text-none"
            @click="storeDevices()"
          >
            {{ $t('button.submit') }}
          </v-btn>
        </v-card-actions>
      </template>
    </card-dialog>
  </div>
</template>

<script>

import EventMedia from '@/components/events/EventMedia.vue'
import timezones from 'timezones-list'
import dayjs from 'dayjs'
import { EventType, EventTypes } from '@/data/models'
import { EventApiService } from '@/data/services'
import _axios from '@/plugins/axios'
import { cloneDeep } from 'lodash'
import { EventBus } from '@/plugins/bus'
import AddEventFeedbackDialog from '@/components/dialogs/AddEventFeedbackDialog.vue'
import EventPlayerDialog from '@/components/dialogs/EventPlayerDialog.vue'
import CardDialog from '@/components/helpers/CardDialog.vue'
import Subtitle from '@/components/helpers/Subtitle.vue'
import EventDrill from '@/components/events/EventDrill.vue'
import EventTrainingProtocol from '@/components/events/EventTrainingProtocol.vue'
import MatchPrepareDialog from '@/components/dialogs/MatchPrepareDialog.vue'
import { getDateTimeFormatForUser, toCamelCase } from '@/utils/helpers'
import EventSourceItem from '@/components/events/EventSourceItem.vue'
import EventNotes from '@/components/events/EventNotes.vue'
import { DeviceApiService } from '@/data/services/DeviceApiService'
import LineChart from '@/components/charts/LineChart.vue'

export default {
  name: 'EventSidebar',
  components: {
    LineChart,
    EventNotes,
    EventSourceItem,
    MatchPrepareDialog,
    EventTrainingProtocol,
    EventDrill,
    Subtitle,
    CardDialog,
    EventPlayerDialog,
    AddEventFeedbackDialog,
    EventMedia
  },
  props: {
    eventDetails: {
      type: Object,
      default: () => ({})
    },
    value: {
      type: Boolean,
      required: true
    },
    loading: {
      type: Boolean,
      required: true
    },
    eventView: {
      type: Boolean,
      required: true
    }
  },
  data: () => ({
    tab: 'players',
    eventPlayerDialog: {
      visible: false,
      playerId: null
    },
    addEventFeedback: {
      dialog: false,
      player: null
    },
    periodEditor: {
      visible: false,
      players: [],
      periods: [],
      min: 0,
      max: 100,
      periodsSaved: false
    },
    fileUpload: {
      filename: undefined,
      progress: 0,
      submitting: false,
      file: undefined
    },
    devices: {
      loading: false,
      data: [],
      allowedDeviceIds: []
    },
    statsportFileUpload: {
      filename: undefined,
      progress: 0,
      submitting: false,
      file: undefined
    },
    fitogetherFileUpload: {
      filename: undefined,
      progress: 0,
      submitting: false,
      file: undefined
    },
    playertekFileUpload: {
      filename: undefined,
      progress: 0,
      submitting: false,
      file: undefined
    },
    catapultOneFileUpload: {
      filename: undefined,
      progress: 0,
      submitting: false,
      file: undefined
    },
    pitcherogpsFileUpload: {
      filename: undefined,
      progress: 0,
      submitting: false,
      file: undefined
    },
    notesDialog: false,
    submitting: false,
    matchPrepareModal: false,
    devicesDialog: false
  }),
  computed: {
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    drawer: {
      get: function () {
        return this.value
      },
      set: function (value) {
        this.$emit('update:value', value)
      }
    },
    event: {
      get: function () {
        return this.eventDetails
      },
      set: function (value) {
        this.$emit('update:event', value)
      }
    },
    ranges: {
      set: function (value) {
        this.periodEditor.range = value
      },
      get: function () {
        return this.periodEditor.range
      }
    },
    integrations() {
      return this.user.organisation_settings.integrations || []
    },
    fieldwizIntegration() {
      return this.integrations.find(i => i.key === 'fieldwiz')
    },
    statsportIntegration() {
      return this.integrations.find(i => i.key === 'statsport')
    },
    fitogetherIntegration() {
      return this.integrations.find(i => i.key === 'fitogether')
    },
    playertekIntegration() {
      return this.integrations.find(i => i.key === 'playertek')
    },
    catapultOneIntegration() {
      return this.integrations.find(i => i.key === 'catapult_one')
    },
    catapultIntegration() {
      return this.integrations.find(i => i.key === 'catapult')
    },
    pitcherogpsIntegration() {
      return this.integrations.find(i => i.key === 'pitcherogps')
    },
    hasOpenEventApplications() {
      return this.user.organisation_settings.has_open_event_applications_module
    },
    hasGpsModule() {
      return this.user.organisation_settings.has_gps_module || false
    },
    downloadEventPlanLink() {
      return process.env.VUE_APP_API_URL + `/events/${this.event.id}/generate-event-plan`
    },
    downloadMatchPreparationLink() {
      return process.env.VUE_APP_API_URL + `/events/${this.event.id}/generate-match-preparation`
    },
    hasItlModule() {
      return this.user.organisation_settings?.has_itl_module || false
    },
    hasNoPlayers() {
      return this.user.organisation_settings?.has_no_players || false
    }
  },
  watch: {
    event: function () {
      this.loadEventPeriods(this.event.periods)

      if (this.eventPlayerDialog.player) {
        this.eventPlayerDialog.player = this.event.players.find(p => p.id === this.eventPlayerDialog.player.id)
      }
    },
    value: function (value) {
      if (value) this.fetchDevices()
    },
    periodEditor: {
      deep: true,
      handler: function () {
        this.updatePlayerPeriodItems()
      }
    }
  },
  mounted() {
    if (this.eventDetails.periods) this.loadEventPeriods(this.eventDetails.periods)
  },
  created() {
    this.timezones = timezones
    this.timezone = dayjs.tz.guess()
    this.eventTypes = EventTypes
    this.eventTypeMatch = EventType.MATCH
    this.eventTypeTraining = EventType.TRAINING
  },
  methods: {
    getDateTimeFormatForUser,
    toCamelCase,
    dayjs,
    loadEventPeriods(periods) {
      this.periodEditor.periods = periods.map(period => {
        return {
          id: period.id,
          title: period.title,
          range: [this.$dayjs(period.start_at).diff(this.$dayjs(this.event.start_at), 'seconds'), this.$dayjs(period.end_at).diff(this.$dayjs(this.event.start_at), 'seconds')],
          is_play_time: period.is_play_time
        }
      })

      if (this.periodEditor.periods.length > 0) this.periodEditor.periodsSaved = true
    },
    handleFileChange(e) {
      this.fileUpload.filename = undefined
      this.fileUpload.submitting = false
      this.fileUpload.progress = 0

      this.fileUpload.filename = e.target.files[0].name
      this.fileUpload.file = e.target.files[0]

      const extension = this.fileUpload.filename.split('.').pop()

      if (extension.toLowerCase() !== 'dkg' && extension.toLowerCase() !== 'asi') {
        this.$store.dispatch(this.$storeActions.ALERT, {
          color: 'error',
          text: this.$t('event_details.media_upload.error.file_type.dkg')
        })
        return
      }

      this.processUpload()
    },
    handleStatsportFileChange(e) {
      this.statsportFileUpload.filename = undefined
      this.statsportFileUpload.submitting = false
      this.statsportFileUpload.progress = 0

      this.statsportFileUpload.filename = e.target.files[0].name
      this.statsportFileUpload.file = e.target.files[0]

      const extension = this.statsportFileUpload.filename.split('.').pop()

      if (extension.toLowerCase() !== 'csv') {
        this.$store.dispatch(this.$storeActions.ALERT, {
          color: 'error',
          text: this.$t('event_details.media_upload.error.file_type.csv')
        })
        return
      }

      this.processStatsportUpload()
    },
    handleFitogetherFileChange(e) {
      this.fitogetherFileUpload.filename = undefined
      this.fitogetherFileUpload.submitting = false
      this.fitogetherFileUpload.progress = 0

      this.fitogetherFileUpload.filename = e.target.files[0].name
      this.fitogetherFileUpload.file = e.target.files[0]

      const extension = this.fitogetherFileUpload.filename.split('.').pop()

      if (extension.toLowerCase() !== 'csv') {
        this.$store.dispatch(this.$storeActions.ALERT, {
          color: 'error',
          text: this.$t('event_details.media_upload.error.file_type.csv')
        })
        return
      }

      this.processFitogetherUpload()
    },
    handlePlayertekFileChange(e) {
      this.playertekFileUpload.filename = undefined
      this.playertekFileUpload.submitting = false
      this.playertekFileUpload.progress = 0

      this.playertekFileUpload.filename = e.target.files[0].name
      this.playertekFileUpload.file = e.target.files[0]

      const extension = this.playertekFileUpload.filename.split('.').pop()

      if (extension.toLowerCase() !== 'csv') {
        this.$store.dispatch(this.$storeActions.ALERT, {
          color: 'error',
          text: this.$t('event_details.media_upload.error.file_type.csv')
        })
        return
      }

      this.processPlayertekUpload()
    },
    handleCatapultOneFileChange(e) {
      this.catapultOneFileUpload.filename = undefined
      this.catapultOneFileUpload.submitting = false
      this.catapultOneFileUpload.progress = 0

      this.catapultOneFileUpload.filename = e.target.files[0].name
      this.catapultOneFileUpload.file = e.target.files[0]

      const extension = this.catapultOneFileUpload.filename.split('.').pop()

      if (extension.toLowerCase() !== 'csv') {
        this.$store.dispatch(this.$storeActions.ALERT, {
          color: 'error',
          text: this.$t('event_details.media_upload.error.file_type.csv')
        })
        return
      }

      this.processCatapultOneUpload()
    },
    handlePitcherogpsFileChange(e) {
      this.pitcherogpsFileUpload.filename = undefined
      this.pitcherogpsFileUpload.submitting = false
      this.pitcherogpsFileUpload.progress = 0

      this.pitcherogpsFileUpload.filename = e.target.files[0].name
      this.pitcherogpsFileUpload.file = e.target.files[0]

      const extension = this.pitcherogpsFileUpload.filename.split('.').pop()

      if (extension.toLowerCase() !== 'xlsx') {
        this.$store.dispatch(this.$storeActions.ALERT, {
          color: 'error',
          text: this.$t('event_details.media_upload.error.file_type.xlsx')
        })
        return
      }

      this.processPitcherogpsUpload()
    },
    async processUpload() {
      this.fileUpload.submitting = true

      try {
        const formData = new FormData()
        formData.append('file', this.fileUpload.file, this.fileUpload.filename)

        await _axios.post(`events/${this.event.id}/fieldwizz-upload`, formData, {
          onUploadProgress: (e) => {
            this.fileUpload.progress = Number((e.loaded / e.total) * 100)
          }
        })

        this.$store.dispatch(this.$storeActions.ALERT, {
          color: 'success',
          text: this.$t('alert.dkg_file_uploaded_processed.success')
        })

        this.fileUpload.filename = undefined
        this.fileUpload.submitting = false
        this.fileUpload.progress = 0

        this.$emit('update')
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          color: 'error',
          text: this.$t('event_details.media_upload.error.DKG')
        })
      } finally {
        this.fileUpload.submitting = false
        this.fileUpload.progress = 0
        this.$refs.uploadFile.value = ''
      }
    },
    async processStatsportUpload() {
      this.statsportFileUpload.submitting = true

      try {
        const formData = new FormData()
        formData.append('file', this.statsportFileUpload.file, this.statsportFileUpload.filename)

        await _axios.post(`events/${this.event.id}/statsport-upload`, formData, {
          onUploadProgress: (e) => {
            this.statsportFileUpload.progress = Number((e.loaded / e.total) * 100)
          }
        })

        this.$store.dispatch(this.$storeActions.ALERT, {
          color: 'success',
          text: this.$t('alert.statsports_csv_uploadaed_processed.success')
        })

        this.statsportFileUpload.filename = undefined
        this.statsportFileUpload.submitting = false
        this.statsportFileUpload.progress = 0

        this.openPeriodEditor()
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          color: 'error',
          text: this.$t('event_details.media_upload.error.statsport')
        })
      } finally {
        this.statsportFileUpload.submitting = false
        this.statsportFileUpload.progress = 0
        this.$refs.statsportUploadFile.value = ''
      }
    },
    async processFitogetherUpload() {
      this.fitogetherFileUpload.submitting = true

      try {
        const formData = new FormData()
        formData.append('file', this.fitogetherFileUpload.file, this.fitogetherFileUpload.filename)

        await _axios.post(`events/${this.event.id}/fitogether-upload`, formData, {
          onUploadProgress: (e) => {
            this.fitogetherFileUpload.progress = Number((e.loaded / e.total) * 100)
          }
        })

        this.$store.dispatch(this.$storeActions.ALERT, {
          color: 'success',
          text: this.$t('alert.fitogether_csv_uploaded_processed.success')
        })

        this.fitogetherFileUpload.filename = undefined
        this.fitogetherFileUpload.submitting = false
        this.fitogetherFileUpload.progress = 0

        this.openPeriodEditor()
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          color: 'error',
          text: this.$t('event_details.media_upload.error.fitogether')
        })
      } finally {
        this.fitogetherFileUpload.submitting = false
        this.fitogetherFileUpload.progress = 0
        this.$refs.fitogetherUploadFile.value = ''
      }
    },
    async processPlayertekUpload() {
      this.playertekFileUpload.submitting = true

      try {
        const formData = new FormData()
        formData.append('file', this.playertekFileUpload.file, this.playertekFileUpload.filename)

        await _axios.post(`events/${this.event.id}/playertek-upload`, formData, {
          onUploadProgress: (e) => {
            this.playertekFileUpload.progress = Number((e.loaded / e.total) * 100)
          }
        })

        this.$store.dispatch(this.$storeActions.ALERT, {
          color: 'success',
          text: this.$t('alert.playertek_csv_uploaded_processed.success')
        })

        this.playertekFileUpload.filename = undefined
        this.playertekFileUpload.submitting = false
        this.playertekFileUpload.progress = 0

        this.openPeriodEditor()
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          color: 'error',
          text: this.$t('event_details.media_upload.error.playertek')
        })
      } finally {
        this.playertekFileUpload.submitting = false
        this.playertekFileUpload.progress = 0
        this.$refs.playertekUploadFile.value = ''
      }
    },
    async processCatapultOneUpload() {
      this.catapultOneFileUpload.submitting = true

      try {
        const formData = new FormData()
        formData.append('file', this.catapultOneFileUpload.file, this.catapultOneFileUpload.filename)

        await _axios.post(`events/${this.event.id}/catapult-one-upload`, formData, {
          onUploadProgress: (e) => {
            this.catapultOneFileUpload.progress = Number((e.loaded / e.total) * 100)
          }
        })

        this.$store.dispatch(this.$storeActions.ALERT, {
          color: 'success',
          text: this.$t('alert.catapult_one_csv_uploaded_processed.success')
        })

        this.catapultOneFileUpload.filename = undefined
        this.catapultOneFileUpload.submitting = false
        this.catapultOneFileUpload.progress = 0

        this.openPeriodEditor()
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          color: 'error',
          text: this.$t('event_details.media_upload.error.catapult_one')
        })
      } finally {
        this.catapultOneFileUpload.submitting = false
        this.catapultOneFileUpload.progress = 0
        this.$refs.catapultOneUploadFile.value = ''
      }
    },
    async processPitcherogpsUpload() {
      this.pitcherogpsFileUpload.submitting = true

      try {
        const formData = new FormData()
        formData.append('file', this.pitcherogpsFileUpload.file, this.pitcherogpsFileUpload.filename)

        await _axios.post(`events/${this.event.id}/pitcherogps-upload`, formData, {
          onUploadProgress: (e) => {
            this.pitcherogpsFileUpload.progress = Number((e.loaded / e.total) * 100)
          }
        })

        this.$store.dispatch(this.$storeActions.ALERT, {
          color: 'success',
          text: this.$t('alert.pitcherogps_uploaded_processed.success')
        })

        this.pitcherogpsFileUpload.filename = undefined
        this.pitcherogpsFileUpload.submitting = false
        this.pitcherogpsFileUpload.progress = 0

        this.openPeriodEditor()
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          color: 'error',
          text: this.$t('event_details.media_upload.error.pitcherogps')
        })
      } finally {
        this.pitcherogpsFileUpload.submitting = false
        this.pitcherogpsFileUpload.progress = 0
        this.$refs.pitcherogpsUploadFile.value = ''
      }
    },
    openPeriodEditor() {
      if (!this.event) return

      const event = this.event

      this.fetchDevices()

      this.loadEventPeriods(event.periods)

      this.periodEditor.max = this.$dayjs(event.end_at).diff(this.$dayjs(event.start_at), 'second')
      this.periodEditor.min = 0

      this.periodEditor.visible = true

      this.periodEditor.players = cloneDeep(this.event.players).map(player => {
        const playerPeriods = this.event.player_periods.filter(p => {
          return p.player_ids.indexOf(player.id) !== -1
        })

        return {
          ...player,
          periods: playerPeriods.length > 0 ? playerPeriods.map(playerPeriod => {
            return {
              id: playerPeriod.id,
              parent_period_index: this.periodEditor.periods.map(p => p.id).indexOf(playerPeriod.parent_period_event_id),
              range: [this.$dayjs(playerPeriod.start_at).diff(this.$dayjs(this.event.start_at), 'seconds'), this.$dayjs(playerPeriod.end_at).diff(this.$dayjs(this.event.start_at), 'seconds')],
              is_play_time: playerPeriod.is_play_time
            }
          }) : [],
          edit_periods: playerPeriods.length > 0,
          downsampledData: {},
          showDownsampledData: false
        }
      })
    },
    addPeriod() {
      const event = this.event
      const maxRange = this.$dayjs(event.end_at).diff(this.$dayjs(event.start_at), 'second')
      const totalPeriods = this.periodEditor.periods.length + 1

      const step = Math.round(maxRange / totalPeriods)

      for (let i = 0; i <= totalPeriods - 2; i++) {
        this.periodEditor.periods[i].range = [i * step, i * step + step]
      }

      this.periodEditor.periods.push({
        title: this.$t('button.period') + ' ' + totalPeriods,
        range: [maxRange - step, maxRange],
        is_play_time: false
      })
    },
    addPlayerPeriod(player) {
      const event = this.event
      const maxRange = this.$dayjs(event.end_at).diff(this.$dayjs(event.start_at), 'second')
      const totalPeriods = player.periods.length + 1

      const step = Math.round(maxRange / totalPeriods)

      for (let i = 0; i <= totalPeriods - 2; i++) {
        player.periods[i].range = [i * step, i * step + step]
      }

      const availableIndexes = Array.from({ length: totalPeriods }, (_, i) => i).filter(i => !player.periods.some(p => p.parent_period_index === i))

      player.periods.push({
        parent_period_index: availableIndexes[0],
        range: [maxRange - step, maxRange],
        is_play_time: false
      })
    },
    editPlayerPeriods(player) {
      player.periods = cloneDeep(this.periodEditor.periods)
      player.periods.forEach((period, index) => {
        period.parent_period_index = index
      })
      player.edit_periods = true
    },
    deletePeriod(index) {
      this.periodEditor.periods.splice(index, 1)
      this.periodEditor.players.forEach(player => {
        const foundIndex = player.periods.findIndex(p => p.parent_period_index === index)
        if (foundIndex !== -1) {
          player.periods.splice(foundIndex, 1)
        }
        player.periods.forEach(period => {
          if (period.parent_period_index > index) {
            period.parent_period_index -= 1
          }
        })
      })
    },
    deletePlayerPeriod(player, index) {
      player.periods.splice(index, 1)
    },
    inputStart(index, value, unit) {
      const date = this.$dayjs(this.event.start_at).add(this.periodEditor.periods[index].range[0], 'seconds')

      this.periodEditor.periods[index].range = [date.set(unit, value).diff(this.$dayjs(this.event.start_at), 'second'), this.periodEditor.periods[index].range[1]]
    },
    inputPlayerStart(player, index, value, unit) {
      const date = this.$dayjs(this.event.start_at).add(player.periods[index].range[0], 'seconds')

      player.periods[index].range = [date.set(unit, value).diff(this.$dayjs(this.event.start_at), 'second'), player.periods[index].range[1]]
    },
    inputEnd(index, value, unit) {
      const date = this.$dayjs(this.event.start_at).add(this.periodEditor.periods[index].range[1], 'seconds')

      this.periodEditor.periods[index].range = [this.periodEditor.periods[index].range[0], date.set(unit, value).diff(this.$dayjs(this.event.start_at), 'second')]
    },
    inputPlayerEnd(player, index, value, unit) {
      const date = this.$dayjs(this.event.start_at).add(player.periods[index].range[1], 'seconds')

      player.periods[index].range = [player.periods[index].range[0], date.set(unit, value).diff(this.$dayjs(this.event.start_at), 'second')]
    },
    async fetchDevices() {
      if (!this.fieldwizIntegration) return
      this.devices.loading = true

      try {
        const response = await DeviceApiService.getAll()

        this.devices.data = response.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.fetching_devices.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.devices.loading = false
      }
    },
    async storePeriods() {
      if (!this.event) return

      this.submitting = true

      try {
        const startAt = this.$dayjs(this.event.start_at)

        const data = {
          players: this.periodEditor.players.map(player => {
            return {
              player_id: player.id,
              periods: player.periods.map(period => {
                return {
                  id: period.id,
                  parent_period_index: period.parent_period_index,
                  start_at: startAt.add(period.range[0], 'seconds').toISOString(),
                  end_at: startAt.add(period.range[1], 'seconds').toISOString(),
                  is_play_time: period.is_play_time
                }
              })
            }
          }),
          periods: this.periodEditor.periods.map(period => {
            return {
              id: period.id,
              title: period.title,
              start_at: startAt.add(period.range[0], 'seconds').toISOString(),
              end_at: startAt.add(period.range[1], 'seconds').toISOString(),
              is_play_time: period.is_play_time
            }
          })
        }

        await EventApiService.storeEventPeriods(this.event.id, data)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.periods_submitted.success'),
          color: 'success'
        })

        this.periodEditor.visible = false
        this.periodEditor.periodsSaved = true

        this.$emit('update')
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.something_went_wrong'),
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async fetchDownsampledData(player) {
      if (!this.event) return

      player.loading = true

      try {
        player.downsampledData = await EventApiService.getGpsDataDownsampled(this.event.id, {
          player_id: player.id
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.something_went_wrong'),
          color: 'error'
        })
        throw e
      } finally {
        player.loading = false
      }
    },
    getSpeedSeries(data) {
      const speed = data.speed
      const timestamps = data.timestamp

      const series = []
      const dateStart = dayjs(this.event.start_at)
      const dateEnd = dayjs(this.event.end_at)

      const totalDurationSeconds = dateEnd.diff(dateStart, 'seconds')

      for (let secondIndex = 0; secondIndex <= totalDurationSeconds; secondIndex++) {
        const currentTimestamp = dateStart.add(secondIndex, 'second')

        const speedValue = speed[secondIndex] !== undefined ? speed[secondIndex] : null

        const timestampValue = timestamps[secondIndex] ? timestamps[secondIndex] * 1000 : currentTimestamp.valueOf()

        series.push([timestampValue, speedValue])
      }

      return series
    },
    async storeDevices() {
      if (!this.event) return

      this.submitting = true

      try {
        const data = {
          devices: this.event.players.map(player => {
            return {
              player_id: player.id,
              device_id: player.device_id
            }
          })
        }

        await EventApiService.storeEventPlayerDevices(this.event.id, data)

        if (this.event.fieldwiz_id) {
          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.manage_devices_calculations.success'),
            color: 'success'
          })
        } else {
          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.manage_devices.success'),
            color: 'success'
          })
        }

        this.devicesDialog = false

        this.$emit('update')
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.something_went_wrong'),
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    editEvent(event) {
      EventBus.$emit(this.$events.EDIT_EVENT, event)
    },
    async togglePlayerFeedbackNotification(player, value) {
      if (!this.event) return

      try {
        await EventApiService.togglePlayerFeedbackNotification(this.event.id, player.id, value)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.something_went_wrong_notification.error'),
          color: 'error'
        })
        throw e
      }
    },
    async regeneratePdfReport() {
      if (!this.event) return

      try {
        await EventApiService.generatePdfReport(this.event.id)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.generate_pdf_report.success'),
          color: 'success'
        })

        this.event.pdf_report_download_url = null

        setTimeout(() => {
          this.$emit('update')
        }, 4000)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.generate_pdf_report.error'),
          color: 'error'
        })
        throw e
      }
    },
    htmlToText(html) {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = html
      return tempDiv.textContent || tempDiv.innerText || ''
    },
    getIconForMatchRole(matchRole) {
      if (matchRole === 'FT' || matchRole === 'SL') {
        return 'fa-shirt'
      } else if (matchRole === 'SUB' || matchRole === 'BEN') {
        return 'fa-exchange-alt'
      } else if (matchRole === 'OUT') {
        return 'fa-user-slash'
      } else {
        return ''
      }
    },
    launchTrainingProgramInstance(instanceId) {
      EventBus.$emit(this.$events.EDIT_TRAINING_PROGRAM_INSTANCE, instanceId)
    },
    updatePlayerPeriodItems() {
      this.periodEditor.players.forEach(player => {
        player.periods.forEach(period => {
          period.availablePeriods = this.periodEditor.periods
            .map((p, ind) => ({ index: ind, title: p.title }))
            .filter(p => player.periods.findIndex(pp => pp.parent_period_index === p.index) === -1 ||
              period.parent_period_index === p.index)
        })
      })
    }
  }

}
</script>

<style scoped>

</style>
